import { useEffect, useState } from "react";
import { BookMarked, ChevronLeft, ChevronRight, Images, Maximize2, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api, TELEGRAM } from "../lib/api";
import { Reveal, Item, Stagger } from "../components/Reveal";

const WM_TEXT = "t.me/Research_Network";

export const Watermark = ({ count = 12, onImage = false, className = "" }) => (
  <div className={`watermark ${onImage ? "on-image" : ""} ${className}`} aria-hidden>
    {Array.from({ length: count }).map((_, i) => <span key={i}>{WM_TEXT}</span>)}
  </div>
);

export const achievementImages = (a) => (a.images?.length ? a.images : a.image ? [a.image] : []);

/** Full-screen viewer: the whole image is visible (object-fit: contain) with prev/next. */
const Lightbox = ({ images, index, onClose, onMove }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") onMove(1); if (e.key === "ArrowLeft") onMove(-1); };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onMove]);
  return (
    <div className="lightbox" onMouseDown={(e) => e.target === e.currentTarget && onClose()} data-testid="lightbox">
      <button className="lb-close" onClick={onClose} data-testid="lightbox-close"><X size={20} /></button>
      {images.length > 1 && <button className="lb-nav prev" onClick={() => onMove(-1)} data-testid="lightbox-prev"><ChevronLeft size={26} /></button>}
      <div className="lb-stage">
        <figure className="lb-figure" data-testid="lightbox-figure">
          <img src={images[index]} alt="" draggable={false} data-testid="lightbox-image" />
          <Watermark count={30} onImage className="lb-watermark" />
          <figcaption className="lb-rights" data-testid="lightbox-rights">© RESEARCHNETWORK · {WM_TEXT} · جميع الحقوق محفوظة</figcaption>
        </figure>
      </div>
      {images.length > 1 && <button className="lb-nav next" onClick={() => onMove(1)} data-testid="lightbox-next"><ChevronRight size={26} /></button>}
      {images.length > 1 && <span className="lb-counter">{index + 1} / {images.length}</span>}
    </div>
  );
};

const AchievementCard = ({ a, onOpen }) => {
  const { t } = useLang();
  const images = achievementImages(a);
  const [idx, setIdx] = useState(0);
  const [showJournal, setShowJournal] = useState(false);
  return (
    <Item className="ach-card" data-testid={`achievement-${a.id}`}>
      {images.length > 0 && (
        <div className="ach-img" onClick={() => onOpen(images, idx)} role="button" title={t.ach.view} data-testid={`achievement-image-${a.id}`}>
          <img src={images[idx]} alt={a.title} loading="lazy" draggable={false} />
          <Watermark count={6} onImage />
          <span className="ach-zoom"><Maximize2 size={15} /></span>
          {images.length > 1 && <span className="ach-count"><Images size={13} />{images.length} {t.ach.photos}</span>}
        </div>
      )}
      {images.length > 1 && (
        <div className="ach-thumbs" data-testid={`achievement-thumbs-${a.id}`}>
          {images.map((src, i) => <button key={i} className={i === idx ? "active" : ""} onClick={() => setIdx(i)} data-testid={`achievement-thumb-${a.id}-${i}`}><img src={src} alt="" draggable={false} /></button>)}
        </div>
      )}
      <div className="ach-body">
        <h3 data-testid={`achievement-title-${a.id}`}>{a.title}</h3>
        {a.description && <p data-testid={`achievement-description-${a.id}`}>{a.description}</p>}
        <div className="ach-meta">
          {a.journal && <button className={`journal-btn ${showJournal ? "open" : ""}`} onClick={() => setShowJournal(!showJournal)} data-testid={`achievement-journal-${a.id}`}><BookMarked size={14} />{t.ach.journal}</button>}
          {a.date_label && <span className="badge badge-muted">{a.date_label}</span>}
        </div>
        {a.journal && showJournal && <div className="journal-box" data-testid={`achievement-journal-value-${a.id}`}><small>{t.ach.journal}</small><b>{a.journal}</b></div>}
      </div>
    </Item>
  );
};

export default function AchievementsPage() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [viewer, setViewer] = useState(null);
  useEffect(() => { api.get("/achievements").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const move = (d) => setViewer((v) => v && { ...v, index: (v.index + d + v.images.length) % v.images.length });

  return (
    <main data-testid="achievements-page" style={{ position: "relative" }} onContextMenu={(e) => e.preventDefault()}>
      <Watermark count={15} className="page-watermark" />
      <div className="container" style={{ position: "relative" }}>
        <Reveal className="page-head"><span className="kicker">RESEARCHNETWORK</span><h1>{t.ach.title}</h1><p>{t.ach.sub}</p></Reveal>
        {items === null ? <div className="page-loader"><span className="loader" /></div>
          : items.length === 0 ? <div className="empty" data-testid="achievements-empty">{t.ach.empty}<br /><a href={TELEGRAM} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm" style={{ marginTop: 14 }}>Telegram</a></div>
          : <Stagger className="ach-grid" stagger={0.06}>{items.map((a) => <AchievementCard key={a.id} a={a} onOpen={(images, index) => setViewer({ images, index })} />)}</Stagger>}
        <div style={{ height: 80 }} />
      </div>
      {viewer && <Lightbox images={viewer.images} index={viewer.index} onClose={() => setViewer(null)} onMove={move} />}
    </main>
  );
}
